import { Trans, useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import type { Placement } from '@floating-ui/react';

import { cn } from 'support/helpers/cn/cn';
import { FEATURE_FLAGS } from 'support/helpers/featureFlags/featureFlags';

import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip';

type AIBadgeProps = {
  className?: string;
  learnMoreHref?: string;
  tooltipPlacement?: Placement;
};

export function AIBadge({ className, learnMoreHref, tooltipPlacement }: AIBadgeProps) {
  const { t } = useTranslation();
  const href = learnMoreHref ?? t('transactions:aiBadge.learnMoreUrl');
  const isEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.USAGE_AND_MODULES);

  if (!isEnabled) {
    return null;
  }

  return (
    <Tooltip placement={tooltipPlacement}>
      <TooltipTrigger>
        <span
          className={cn(
            'box-border inline-flex h-5 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 font-medium  text-gray-800',
            className ?? '',
          )}
        >
          <span className="inline-block size-[15px]">
            <AISparkIcon className="size-full" />
          </span>
          <p className="text-xs leading-4 font-medium">{t('transactions:aiBadge.label')}</p>
        </span>
      </TooltipTrigger>
      <TooltipContent onClick={(e) => e.stopPropagation()}>
        <Trans
          i18nKey="transactions:aiBadge.tooltip"
          components={{
            a: <a href={href} target="_blank" rel="noreferrer" className="underline hover:text-gray-200" />,
          }}
        />
      </TooltipContent>
    </Tooltip>
  );
}

const AISparkIcon = ({ className }: { className: string }) => (
  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <g filter="url(#filter0_d_2132_58738)">
      <path
        d="M15.2452 8.78673C11.2806 9.27424 10.2743 10.2805 9.78667 14.2453C9.745 14.5849 9.25488 14.5849 9.21321 14.2453C8.7257 10.2806 7.71943 9.27434 3.75464 8.78673C3.41504 8.74506 3.41504 8.25494 3.75464 8.21327C7.7193 7.72576 8.72559 6.71949 9.21321 2.7547C9.25488 2.4151 9.745 2.4151 9.78667 2.7547C10.2742 6.71936 11.2804 7.72566 15.2452 8.21327C15.5848 8.25494 15.5848 8.74506 15.2452 8.78673Z"
        fill="url(#paint0_linear_2132_58738)"
      />
    </g>
    <g filter="url(#filter1_d_2132_58738)">
      <path
        d="M7.72454 2.57128C7.98231 0.47624 11.0175 0.476241 11.2753 2.57128C11.5164 4.53168 11.8537 5.30886 12.2724 5.72753C12.6911 6.14618 13.4682 6.4835 15.4286 6.7246H15.4277C17.5234 6.98175 17.5239 10.0176 15.4286 10.2754C13.4682 10.5164 12.6911 10.8538 12.2724 11.2725C11.8537 11.6911 11.5164 12.4683 11.2753 14.4287C11.0175 16.5237 7.98231 16.5237 7.72454 14.4287L7.62981 13.7471C7.39801 12.2794 7.0938 11.6388 6.72747 11.2725C6.30879 10.8538 5.53163 10.5165 3.57122 10.2754V10.2744C1.4763 10.0165 1.47622 6.98237 3.57122 6.7246L4.25286 6.62987C5.72054 6.39807 6.36114 6.09386 6.72747 5.72753C7.14613 5.30884 7.48344 4.53169 7.72454 2.57128ZM9.78704 2.75487C9.74537 2.41528 9.25449 2.41528 9.21282 2.75487C8.7252 6.7194 7.71921 7.72537 3.75481 8.21288C3.41522 8.25455 3.41522 8.74543 3.75481 8.7871C7.71934 9.27472 8.72531 10.2807 9.21282 14.2451C9.25449 14.5847 9.74537 14.5847 9.78704 14.2451C10.2747 10.2806 11.2807 9.27461 15.245 8.7871C15.5846 8.74543 15.5846 8.25455 15.245 8.21288C11.2805 7.72526 10.2746 6.71927 9.78704 2.75487Z"
        fill="white"
      />
    </g>
    <defs>
      <filter
        id="filter0_d_2132_58738"
        x="1.49994"
        y="1.5"
        width="16"
        height="16"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="1" />
        <feGaussianBlur stdDeviation="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2132_58738" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2132_58738" result="shape" />
      </filter>
      <filter
        id="filter1_d_2132_58738"
        x="0"
        y="0"
        width="18.9998"
        height="19"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="1" />
        <feGaussianBlur stdDeviation="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2132_58738" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2132_58738" result="shape" />
      </filter>
      <linearGradient
        id="paint0_linear_2132_58738"
        x1="9.49994"
        y1="2.5"
        x2="9.49994"
        y2="14.5"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0ED6A9" />
        <stop offset="1" stopColor="#818CF8" />
      </linearGradient>
    </defs>
  </svg>
);
